import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Button from "../../../components/button/AdminActionsButton";
import ColorSelector from "../../../components/input/ColorSelector";
import Dropdown from "../../../components/input/Dropdown";
import ImageUpload from "../../../components/input/ImageUpload";
import InputfieldWithDropDown from "../../../components/input/InputfieldWithDropDown";
import SizeSelector from "../../../components/input/SizeSelector";
import AdminSidebar from "../../../components/sidebar/AdminSidebar";
import { usePopup } from "../../../context/PopupContext";
import { productService } from "../../../service/ProductService";
import { categories } from "../../../store/categoryStore";

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const Nav = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: 10;

  @media (max-width: 1024px) {
    position: relative;
    height: auto;
    width: 100%;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  margin-left: 260px;
  width: calc(100% - 260px);

  @media (max-width: 1200px) {
    margin-left: 80px;
    width: calc(100% - 80px);
    padding: 15px;
  }

  @media (max-width: 1024px) {
    margin-left: 0;
    width: 100%;
    padding: 10px;
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;

  @media (max-width: 768px) {
    padding: 20px 15px;
    border-radius: 8px;
  }
`;

const Title = styled.h1`
  text-align: center;
  font-size: 28px;
  font-weight: 600;
  margin-bottom: 30px;
  color: #1a1a1a;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FormSection = styled.div`
  grid-column: ${(props) => (props.fullWidth ? "1 / -1" : "auto")};
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 15px;
  resize: vertical;
  min-height: 100px;
  transition:
    border 0.2s,
    box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #666;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }
`;

const ButtonContainer = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 15px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const ImageUploadContainer = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-around;
  gap: 15px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const FixedInputWrapper = styled.div`
  width: 100%;

  & > div {
    width: 100%;
  }

  input,
  select {
    width: 100%;
    box-sizing: border-box;
  }
`;

function AddProductsPage() {
  const [productData, setProductData] = useState({
    name: "",
    description: "",
    categoryName: "",
    gender: "",
    originalPrice: "",
    price: "",
    quantity: "",
    material: "",
    discount: 0,
    sizes: [],
    colors: [],
    images: [],
  });
  const navigate = useNavigate();
  const { showPopup } = usePopup();

  const handleInputChange = (field, value) => {
    setProductData((prev) => {
      let updatedData = { ...prev, [field]: value };

      if (field === "originalPrice" || field === "discount") {
        const originalPrice = parseFloat(
          field === "originalPrice" ? value : prev.originalPrice,
        );
        const discount = parseFloat(
          field === "discount" ? value : prev.discount,
        );
        if (!isNaN(originalPrice)) {
          const discountedPrice = !isNaN(discount)
            ? originalPrice - (originalPrice * discount) / 100
            : originalPrice;
          updatedData.price = discountedPrice.toFixed(0);
        }
      }

      return updatedData;
    });
  };

  const handleProductUpload = () => {
    const formData = new FormData();

    Object.entries(productData).forEach(([key, value]) => {
      if (key === "sizes" || key === "colors") {
        value.forEach((item) => formData.append(key, item));
      } else if (key === "images") {
        value.forEach((file) => {
          formData.append("images", file);
        });
      } else {
        formData.append(key, value);
      }
    });

    return formData;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = handleProductUpload();

    productService.addProduct(
      formData,
      () => {
        showPopup(
          "New product created successfully.",
          "The product will now be visible.",
        );
        navigate("/admin/products");
      },
      {
        default: (message) => {
          alert(message);
        },
      },
    );
  };

  const handleReset = () => {
    setProductData({
      name: "",
      description: "",
      category: "",
      gender: "",
      originalPrice: "",
      price: "",
      quantity: "",
      material: "",
      discount: "",
      sizes: [],
      colors: [],
      images: [],
    });
  };

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Wrapper>
      <Nav>
        <AdminSidebar collapsed={window.innerWidth < 1200} />
      </Nav>
      <Content>
        <Container>
          <Title>Upload New Product</Title>
          <Form onSubmit={handleSubmit}>
            <ImageUploadContainer>
              <ImageUpload
                onImagesChange={(files) => handleInputChange("images", files)}
              />
            </ImageUploadContainer>

            <FormSection fullWidth>
              <Label>Product Name</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="text"
                  value={productData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Enter product name"
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection fullWidth>
              <Label>Product Description</Label>
              <TextArea
                value={productData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Enter product description"
              />
            </FormSection>

            <FormSection>
              <Label>Category</Label>
              <FixedInputWrapper>
                <Dropdown
                  type="dropdown"
                  value={productData.categoryName}
                  onChange={(value) => handleInputChange("categoryName", value)}
                  options={categories}
                  placeholder="Select or enter category"
                  required
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Gender</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="dropdown"
                  value={productData.gender}
                  onChange={(value) => handleInputChange("gender", value)}
                  options={["Men", "Women", "Unisex", "Kids"]}
                  placeholder="Select gender"
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Original Price</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="number"
                  value={productData.originalPrice}
                  onChange={(e) =>
                    handleInputChange("originalPrice", e.target.value)
                  }
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Discount (%)</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="number"
                  value={productData.discount}
                  onChange={(e) =>
                    handleInputChange("discount", e.target.value)
                  }
                  placeholder="0-80"
                  min="0"
                  max="80"
                  required
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Final Price (after discount)</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="text"
                  value={productData.price}
                  disabled
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Quantity</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="number"
                  value={productData.quantity}
                  onChange={(e) =>
                    handleInputChange("quantity", e.target.value)
                  }
                  placeholder="Enter quantity"
                  min="0"
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection>
              <Label>Material</Label>
              <FixedInputWrapper>
                <InputfieldWithDropDown
                  type="text"
                  value={productData.material}
                  onChange={(e) =>
                    handleInputChange("material", e.target.value)
                  }
                  placeholder="e.g., Cotton, Leather"
                />
              </FixedInputWrapper>
            </FormSection>

            <FormSection fullWidth>
              <Label>Available Sizes</Label>
              <SizeSelector
                selectedSizes={productData.sizes}
                onChange={(sizes) => handleInputChange("sizes", sizes)}
              />
            </FormSection>

            <FormSection>
              <Label>Colors</Label>
              <ColorSelector
                selectedColors={productData.colors}
                onChange={(colors) => handleInputChange("colors", colors)}
              />
            </FormSection>
            <ButtonContainer>
              <Button type="button" variant="secondary" onClick={handleReset}>
                Reset Form
              </Button>
              <Button type="submit" variant="primary">
                Add Product
              </Button>
            </ButtonContainer>
          </Form>
        </Container>
      </Content>
    </Wrapper>
  );
}

export default AddProductsPage;
